/* eslint-disable no-magic-numbers */
import React from 'react';
import {action, observable} from 'mobx';
import {observer, PropTypes as MobxPropTypes} from 'mobx-react';
import PropTypes from 'prop-types';
import {Badge, Modal, ModalBody, ModalHeader} from 'reactstrap';
import classNames from 'classnames';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faStar} from '@fortawesome/free-solid-svg-icons';

import CustomIcon from '../../common/customIcon/CustomIcon';
import inject from '../../hoc/injectHoc';
import sunnyIcon from '../../../assets/icons/sunny_icon.png';
import partlySunnyIcon from '../../../assets/icons/partly_sunny_icon.png';
import rainyIcon from '../../../assets/icons/rainy_icon.png';
import {userPaymentRoute} from '../../routePaths';
import {replaceRouteParams} from '../../../utils/routeHelper';
import {SUPER_ADMIN_ROLE} from '../../../constants/userConstants';
import UpgradeButton from '../../common/upgradeButton/UpgradeButton';

import {getExtendedWeatherLayers} from './extendedWeather';
import CounterModal from './counter/CounterModal';
import SheetModal from './sheet/SheetModal';
import {generateSheetHeaderEntities, parseSheetToCard} from './sheet/utils';

import './selectFeedModal.scss';

/**
 * The default modal title.
 * @const {string}
 */
const DEFAULT_TITLE = 'Select Feed';

/**
 * The SelectFeedModal component.
 */
export class SelectFeedModal extends React.Component {
  /**
   * Determines which Feed folder is open
   */
  @observable selectedFeedFolder = 'all'

  /*
   * Determines if the Counter Modal is open
   */
  @observable isCounterModalOpen = false

  /*
   * Determines if the Sheet Modal is open
   */
  @observable isSheetModalOpen = false

  /**
   * Triggered when the component has just updated.
   *
   * @param {{isOpen: boolean}} prevProps
   */
  componentDidUpdate(prevProps) {
    if (!prevProps.isOpen && this.props.isOpen) {
      this.initializeModal();
    }
  }

  /**
   * Initializes the modal.
   */
  @action initializeModal = () => {
    // Put any initializations here.
    this.toggleCounterModal(false);
    this.toggleSheetModal(false);

    this.props.apiCompanyScheduleSheetsGetPublicStore.refresh({
      // isPaid: true
    }, true);
  };

  /**
   * Sets which feed folder is open.
   * @param {'all' | 'included' | 'premium'} feedFolderKey
   */
  @action setSelectedFeedFolder = (feedFolderKey) => {
    this.selectedFeedFolder = feedFolderKey;
  }

  /**
   * Toggle the Counter modal
   *
   * @param {boolean} isShown
   */
  @action toggleCounterModal = (isShown) => {
    if (typeof isShown === 'boolean') {
      this.isCounterModalOpen = isShown;
    } else {
      this.isCounterModalOpen = !this.isCounterModalOpen;
    }
  };

  /**
   * Toggle the Sheet modal
   *
   * @param {boolean} isShown
   */
  @action toggleSheetModal = (isShown) => {
    if (typeof isShown === 'boolean') {
      this.isSheetModalOpen = isShown;
    } else {
      this.isSheetModalOpen = !this.isSheetModalOpen;
    }
  };

  /**
   * Calculate and generate feed items
   * @param {{}} paidSheets
   * @returns {*}
   */
  generateFeedItems = (paidSheets) => {
    const {resolution} = this.props;

    const staticFeedItems = [
      {
        name: 'Current Temp',
        type: 'Weather',
        isBeta: false,
        placeholder: '100°',
        entities: [
          {
            type: 'Weather',
            entityType: 'feed',
            markdown: '{|}{$1.00}{%1}{100}{#ffffff}{poppinsbold}100°{!poppinsbold}{!#ffffff}{!100}{!%1}{!$1.00}{!|}'
          }
        ],
        isEssential: false,
        isPremium: false,
        onClick: (feedItem) => this.onCompleteModal(feedItem)
      },
      {
        name: 'Current Time',
        type: 'CurrentTime',
        isBeta: false,
        placeholder: '12:00',
        entities: [
          {
            type: 'CurrentTime',
            entityType: 'feed',
            markdown: '{|}{$1.00}{%1}{100}{#ffffff}{poppinsbold}12:00{!poppinsbold}{!#ffffff}{!100}{!%1}{!$1.00}{!|}',
          }
        ],
        isEssential: false,
        isPremium: false,
        onClick: (feedItem) => this.onCompleteModal(feedItem)
      },
      {
        name: 'Current Date',
        type: 'CurrentDate',
        isBeta: false,
        placeholder: 'MM/DD/YYYY',
        entities: [
          {
            type: 'CurrentDate',
            entityType: 'feed',
            markdown: '{|}{$1.00}{%1}{100}{#ffffff}{poppinsbold}MM/DD/YYYY{!poppinsbold}{!#ffffff}{!100}{!%1}{!$1.00}{!|}',
          }
        ],
        isEssential: false,
        isPremium: false,
        onClick: (feedItem) => this.onCompleteModal(feedItem)
      },
      {
        name: '3 Day Forecast',
        type: '3DayForecast',
        isBeta: false,
        placeholder:
          <div className="mx-2 flex-grow-1 row no-gutters">
            <div className="col-4 px-1">
              <div className="forecast-card">
                <p>TODAY</p>
                <img src={sunnyIcon} />
                <p>100°</p>
                <p>80°</p>
              </div>
            </div>
            <div className="col-4 px-1">
              <div className="forecast-card">
                <p>MON</p>
                <img src={rainyIcon} />
                <p>100°</p>
                <p>80°</p>
              </div>
            </div>
            <div className="col-4 px-1">
              <div className="forecast-card">
                <p>TUE</p>
                <img src={partlySunnyIcon} />
                <p>100°</p>
                <p>80°</p>
              </div>
            </div>
          </div>,
        entities: getExtendedWeatherLayers(resolution),
        isEssential: true,
        isPremium: true,
        onClick: (feedItem) => this.onCompleteModal(feedItem)
      },
      {
        name: 'Counter',
        type: 'Counter',
        isBeta: false,
        placeholder:
          <div className="counter">
            <div
              className="counter-inner-container"
            >
              <div>0</div>
              <div>2</div>
              <div>
                3<br />
                4
              </div>
            </div>
          </div>,
        entities: [
          {
            type: 'Counter',
            entityType: 'feed',
            markdown: '{|}{$1.00}{%1}{100}{#ffffff}{poppinsbold}10 days{!poppinsbold}{!#ffffff}{!100}{!%1}{!$1.00}{!|}'
          }
        ],
        isEssential: false,
        isPremium: false,
        onClick: () => this.toggleCounterModal(true)
      },
      {
        name: 'Sheets',
        type: 'Sheets',
        isBeta: true,
        placeholder:
        <span>
          <CustomIcon
            type="sheets"
            style={{
              width: '100px',
              height: '100px',
            }}
            className="mr-2"
          />
        </span>,
        entities: [],
        isEssential: false,
        isPremium: false,
        onClick: () => this.toggleSheetModal(true)
      },
    ];

    const feedItems = staticFeedItems;

    paidSheets.forEach((sheet) => {
      const sheetEntities = generateSheetHeaderEntities(sheet, resolution);
      feedItems.push({
        name: sheet.name,
        type: 'Sheets',
        isBeta: false,
        placeholder: parseSheetToCard(sheet),
        entities: sheetEntities,
        isEssential: true,
        isPremium: false,
        onClick: (feedItem) => this.onCompleteModal(feedItem)
      });
    });

    return feedItems;
  }

  /**
   * Triggered when the modal is closed without finishing.
   */
  onCancelModal = () => {
    const {onComplete} = this.props;

    onComplete(false);
  };

  /**
   * Triggered when the modal is closed after choosing a feed type.
   * @param {{}} feedItem
   */
  @action onCompleteModal = async (feedItem) => {
    const {onComplete} = this.props;

    onComplete(feedItem);
  };

  /**
   * Redirects to the sign up page.
   */
  onUpgrade = () => {
    const {routerStore} = this.props;

    const toRoute = replaceRouteParams(userPaymentRoute, {planId: ''});
    routerStore.push(userPaymentRoute, {
      addTo: toRoute,
    });
  };

  /**
   * Redirects to the sign up page.
   *
   * @param {{}} feedItem
   * @param {boolean} userHasAccessToEssential
   * @param {boolean} userHasAccessToPremium
   * @returns {null}
   */
  onFeedItemClick = (feedItem, userHasAccessToEssential, userHasAccessToPremium) => {
    if (feedItem.isPremium && !userHasAccessToPremium) {
      return null;
    }

    feedItem.onClick(feedItem);
    return null;
  };

  /**
   * Render the feed item
   * @param {{}} feedItem
   * @param {boolean} userHasAccessToEssential
   * @param {boolean} userHasAccessToPremium
   *
   * @returns {{}}
   */
  renderFeedItem = (feedItem, userHasAccessToEssential, userHasAccessToPremium) => {
    const userNeedsUpgrade = (feedItem.isPremium && !userHasAccessToPremium)
      || (feedItem.isEssential && !userHasAccessToEssential);

    return (
      <div key={feedItem.name} className="col-sm-12 col-lg-6 col-xl-4 p-2">
        <div
          className={classNames('d-flex flex-column feed-item', {'upgrade-feed-item': userNeedsUpgrade})}
          onClick={() => this.onFeedItemClick(feedItem, userHasAccessToEssential, userHasAccessToPremium)}
        >
          <div className="p-2">
            <div className="feed-item-thumbnail">
              {feedItem.placeholder}

              {(feedItem.isEssential || feedItem.isPremium)
              && (<Badge className="ml-2 text-light feed-item-badge" color="info">
                <FontAwesomeIcon icon={faStar} />
                 Included in {feedItem.isPremium ? 'Premium' : 'Essential'}
              </Badge>)}
            </div>
          </div>
          <div className="feed-item-info p-3">
            <div className="feed-item-name">
              {feedItem.name}
              {(feedItem.isBeta) && (
                <Badge className="ml-2 text-light" color="primary">
                  Beta
                </Badge>
              )}
            </div>
          </div>
        </div>
        {(userNeedsUpgrade) && <UpgradeButton />}
      </div>
    );
  }

  /**
   * Renders the component.
   *
   * @returns {{}}
   */
  render() {
    const {
      isOpen,
      apiUserGetMeStore,
      apiCompanyScheduleSheetsGetPublicStore,
      resolution,
      title
    } = this.props;

    const user = apiUserGetMeStore.getFulfilled();
    const userIsSuperAdmin = user && user.role === SUPER_ADMIN_ROLE;
    const userHasAccessToEssential = user && user.plan ? user.plan.hasEssentialAccess : false || userIsSuperAdmin;
    const userHasAccessToPremium = user && user.plan ? user.plan.hasPremiumAccess : false || userIsSuperAdmin;

    const publicSheets = apiCompanyScheduleSheetsGetPublicStore.getFulfilled() || [];
    const paidSheets = publicSheets.filter((sheet) => sheet.isPaid || (sheet.sourceSheet && sheet.sourceSheet.isPaid));

    const feedItems = this.generateFeedItems(paidSheets);
    const freeFeedItems = feedItems.filter((feedItem) => !(feedItem.isPremium || feedItem.isEssential));
    const paidFeedItems = feedItems.filter((feedItem) => feedItem.isPremium || feedItem.isEssential);

    const feedItemsToShow = {
      all: feedItems,
      free: freeFeedItems,
      paid: paidFeedItems
    };

    const feedFolderOptions = [
      {key: 'all', label: 'All Feeds'},
      {key: 'free', label: 'Free Feeds'},
      {key: 'paid', label: 'Paid Feeds'}
    ];

    if (!isOpen) {
      return null;
    }

    return (
      <Modal isOpen={isOpen} toggle={this.onCancelModal} className="select-feed-modal" centered>
        <ModalHeader toggle={this.onCancelModal}>{title}</ModalHeader>
        <ModalBody>
          <div>
            <div className="select-feed-main">
              <div className="left-side">
                {feedFolderOptions.map((option) => (<div
                  key={option.key}
                  className={classNames('feed-type-list-item d-flex align-items-center', {active: this.selectedFeedFolder === option.key})}
                  onClick={() => this.setSelectedFeedFolder(option.key)}
                >
                  <div>
                    {option.label}
                  </div>
                  <div className="feed-type-list-item-count ml-auto">
                    {feedItemsToShow[option.key].length}
                  </div>
                </div>))}
              </div>
              <div className="right-side container-fluid">
                <div className="row">
                  {feedItemsToShow[this.selectedFeedFolder].map((feedItem) => this.renderFeedItem(feedItem, userHasAccessToEssential, userHasAccessToPremium))}
                </div>
              </div>
            </div>
          </div>
          {this.isCounterModalOpen && (<CounterModal
            isOpen={this.isCounterModalOpen}
            resolution={resolution}
            onCancelModal={this.toggleCounterModal}
            onComplete={this.onCompleteModal}
          />)}
          {this.isSheetModalOpen && (<SheetModal
            isOpen={this.isSheetModalOpen}
            resolution={resolution}
            onCancelModal={this.toggleSheetModal}
            onComplete={this.onCompleteModal}
            userHasAccessToEssential={userHasAccessToEssential}
          />)}
        </ModalBody>
      </Modal>
    );
  }
}

SelectFeedModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onComplete: PropTypes.func.isRequired,
  resolution: MobxPropTypes.observableObject.isRequired,

  apiCompanyScheduleSheetsGetPublicStore: MobxPropTypes.observableObject,
  apiUserGetMeStore: MobxPropTypes.observableObject,
  routerStore: MobxPropTypes.observableObject,
  title: PropTypes.string,
};

SelectFeedModal.defaultProps = {
  title: DEFAULT_TITLE,
};

SelectFeedModal.wrappedComponent = {};
SelectFeedModal.wrappedComponent.propTypes = {
  apiCompanyScheduleSheetsGetPublicStore: MobxPropTypes.observableObject.isRequired,
  apiUserGetMeStore: MobxPropTypes.observableObject.isRequired,
  resolution: MobxPropTypes.observableObject.isRequired,
  routerStore: MobxPropTypes.observableObject.isRequired,
};

export default inject(SelectFeedModal)(
  observer(SelectFeedModal)
);
